// src/pages/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';

// Eye icon for toggling API key visibility
function EyeIcon({ visible }) {
  return visible ? (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

// Masked API key input with show/hide toggle + copy button
function SecretInput({ label, value, onChange, placeholder, hint }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type={visible ? 'text' : 'password'}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-mono focus:ring-2 focus:ring-tenafi-green outline-none pr-10"
          />
          <button
            type="button"
            onClick={() => setVisible(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title={visible ? 'Hide' : 'Show'}
          >
            <EyeIcon visible={visible} />
          </button>
        </div>
        {value && (
          <button
            type="button"
            onClick={handleCopy}
            className="text-xs text-tenafi-green hover:underline font-medium whitespace-nowrap"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        )}
      </div>
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

// Tag input for keyword lists (escalation triggers, forbidden language)
function TagInput({ tags, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim().toLowerCase();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput('');
  };
  const remove = (t) => onChange(tags.filter(x => x !== t));
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full">
            {t}
            <button type="button" onClick={() => remove(t)} className="text-gray-400 hover:text-red-500 leading-none ml-0.5">✕</button>
          </span>
        ))}
        {tags.length === 0 && <span className="text-xs text-gray-400 italic">None added yet</span>}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder || 'Type and press Enter'}
          className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
        />
        <button type="button" onClick={add}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-sm transition-colors font-medium">
          + Add
        </button>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)} type="button"
      className={"relative inline-flex h-6 w-11 items-center rounded-full transition-colors " +
        (checked ? 'bg-tenafi-green' : 'bg-gray-200')}>
      <span className={"inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
        (checked ? 'translate-x-6' : 'translate-x-1')} />
    </button>
  );
}

const DEFAULT_SETTINGS = {
  defaultTone: 'balanced',
  channels: { sms: true, email: true, voice: false },
  quietHoursStart: '09:00',
  quietHoursEnd: '20:00',
  allowPaymentPlans: true,
  maxInstallments: 3,
  minPaymentAmount: 100,
  defaultLanguage: 'en',
  autoDetectLanguage: true,
};

const DEFAULT_AUTOPILOT_CONFIG = {
  enabled:            false,
  followUpDelayDays:  3,     // days since last outreach before sending auto follow-up
  maxAutoFollowUps:   3,     // max auto follow-ups per tenant per month
  channel:            'sms', // 'sms' | 'email'
  includeHighRisk:    true,
  includeMediumRisk:  true,
  includeLowRisk:     false,
};

const DEFAULT_AGENT_CONFIG = {
  // Contact frequency limits
  maxCallsPerTenant:  3,
  maxSmsPerWeek:      5,
  maxEmailsPerWeek:   7,
  // Risk-based tone instructions (per tier, replaces global tone)
  toneInstructions: {
    low:    'Be warm and friendly. Remind the tenant gently and offer assistance if needed.',
    medium: 'Be professional and clear. State the balance owed and ask for a payment timeline.',
    high:   'Be direct and firm. Emphasize urgency, mention late fees, and request immediate payment.',
  },
  // Escalation rules
  escalateOnDispute:      true,
  escalateOnLegalThreat:  true,
  escalateOnHardship:     false,
  autoEscalateAfterDays:  14,
  escalationKeywords:     ['lawsuit', 'attorney', 'lawyer', 'sue', 'court', 'habitability'],
  forbiddenLanguage:      ['threaten', 'arrest', 'jail', 'criminal'],
  // Promise tracking
  allowPromisesToPay:     true,
  maxPromiseWindowDays:   7,
};

const DEFAULT_VOICE_CONFIG = {
  elevenlabsApiKey: '',
  elevenlabsAgentId: '',
  elevenlabsPhoneNumberId: '',
  twilioFromNumber: '',
  voiceEnabled: false,
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [voiceConfig, setVoiceConfig] = useState(DEFAULT_VOICE_CONFIG);
  const [agentConfig, setAgentConfig] = useState(DEFAULT_AGENT_CONFIG);
  const [autopilot, setAutopilotConfig] = useState(DEFAULT_AUTOPILOT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);
  const [savingAgent, setSavingAgent] = useState(false);
  const [savingAutopilot, setSavingAutopilot] = useState(false);
  const [runningAutopilot, setRunningAutopilot] = useState(false);
  const [autopilotRunResult, setAutopilotRunResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [savedVoice, setSavedVoice] = useState(false);
  const [savedAgent, setSavedAgent] = useState(false);
  const [savedAutopilot, setSavedAutopilot] = useState(false);
  const [showWebhookUrl, setShowWebhookUrl] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      try {
        const [prefSnap, voiceSnap, agentSnap, autopilotSnap] = await Promise.all([
          getDoc(doc(db, 'users', user.uid, 'settings', 'preferences')),
          getDoc(doc(db, 'users', user.uid, 'settings', 'voiceConfig')),
          getDoc(doc(db, 'users', user.uid, 'settings', 'agentConfig')),
          getDoc(doc(db, 'users', user.uid, 'settings', 'autopilot')),
        ]);
        if (prefSnap.exists())  setSettings(prev => ({ ...prev, ...prefSnap.data() }));
        if (voiceSnap.exists()) setVoiceConfig(prev => ({ ...prev, ...voiceSnap.data() }));
        if (agentSnap.exists()) {
          const d = agentSnap.data();
          setAgentConfig(prev => ({
            ...prev,
            ...d,
            // Nested object merge for toneInstructions
            toneInstructions: { ...prev.toneInstructions, ...(d.toneInstructions || {}) },
          }));
        }
        if (autopilotSnap.exists()) setAutopilotConfig(prev => ({ ...prev, ...autopilotSnap.data() }));
      } catch (err) {
        console.error('Load settings error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'users', user.uid, 'settings', 'preferences'), settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVoice = async () => {
    if (!user?.uid) return;
    setSavingVoice(true);
    try {
      await setDoc(doc(db, 'users', user.uid, 'settings', 'voiceConfig'), voiceConfig);
      setSavedVoice(true);
      setTimeout(() => setSavedVoice(false), 3000);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSavingVoice(false);
    }
  };

  const handleSaveAgent = async () => {
    if (!user?.uid) return;
    setSavingAgent(true);
    try {
      await setDoc(doc(db, 'users', user.uid, 'settings', 'agentConfig'), agentConfig);
      setSavedAgent(true);
      setTimeout(() => setSavedAgent(false), 3000);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSavingAgent(false);
    }
  };

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));
  const setChannel = (ch, val) => setSettings(prev => ({
    ...prev, channels: { ...prev.channels, [ch]: val }
  }));
  const setVoice = (key, val) => setVoiceConfig(prev => ({ ...prev, [key]: val }));
  const setAgent = (key, val) => setAgentConfig(prev => ({ ...prev, [key]: val }));
  const setAgentTone = (tier, val) => setAgentConfig(prev => ({
    ...prev,
    toneInstructions: { ...prev.toneInstructions, [tier]: val },
  }));
  const setAuto = (key, val) => setAutopilotConfig(prev => ({ ...prev, [key]: val }));

  const handleSaveAutopilot = async () => {
    if (!user?.uid) return;
    setSavingAutopilot(true);
    try {
      await setDoc(doc(db, 'users', user.uid, 'settings', 'autopilot'), autopilot);
      setSavedAutopilot(true);
      setTimeout(() => setSavedAutopilot(false), 3000);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSavingAutopilot(false);
    }
  };

  const firebaseProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
  const webhookUrl = firebaseProjectId
    ? `https://us-central1-${firebaseProjectId}.cloudfunctions.net/elevenLabsWebhook`
    : 'https://us-central1-YOUR_PROJECT_ID.cloudfunctions.net/elevenLabsWebhook';

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-tenafi-green border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-500 text-sm mt-0.5">Configure your Tenafi preferences</p>
          </div>
          {saved && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-xl text-sm font-medium">
              ✓ Settings saved
            </div>
          )}
        </div>

        {/* Section 1: Outreach Preferences */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-5">Outreach Preferences</h2>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Tone</label>
              <select value={settings.defaultTone} onChange={e => set('defaultTone', e.target.value)}
                className="w-full sm:w-64 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none">
                <option value="friendly">Friendly — warm and understanding</option>
                <option value="balanced">Balanced — professional and clear</option>
                <option value="firm">Firm — direct and urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Channels</label>
              <div className="flex flex-wrap gap-4">
                {[
                  { key: 'sms', label: 'SMS' },
                  { key: 'email', label: 'Email' },
                  { key: 'voice', label: 'Voice Call' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={settings.channels[key] || false}
                      onChange={e => setChannel(key, e.target.checked)}
                      className="rounded accent-tenafi-green" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quiet Hours Start</label>
                <input type="time" value={settings.quietHoursStart} onChange={e => set('quietHoursStart', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quiet Hours End</label>
                <input type="time" value={settings.quietHoursEnd} onChange={e => set('quietHoursEnd', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none" />
              </div>
            </div>
            <p className="text-xs text-gray-400 -mt-2">Messages will not be sent outside these hours</p>
          </div>
        </div>

        {/* Section 2: Payment Plans */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-5">Payment Plans</h2>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Allow Payment Plans</p>
                <p className="text-xs text-gray-400 mt-0.5">Let tenants split balances into installments</p>
              </div>
              <Toggle checked={settings.allowPaymentPlans} onChange={v => set('allowPaymentPlans', v)} />
            </div>
            <div className={"space-y-4 transition-opacity " + (!settings.allowPaymentPlans ? 'opacity-40 pointer-events-none' : '')}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Installments</label>
                <select value={settings.maxInstallments} onChange={e => set('maxInstallments', parseInt(e.target.value))}
                  className="w-40 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none">
                  {[2,3,4,6].map(n => <option key={n} value={n}>{n} payments</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Payment Amount</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input type="number" value={settings.minPaymentAmount} min={25}
                    onChange={e => set('minPaymentAmount', parseInt(e.target.value) || 100)}
                    className="w-32 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Language */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-5">Language</h2>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Outreach Language</label>
              <select value={settings.defaultLanguage} onChange={e => set('defaultLanguage', e.target.value)}
                className="w-48 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none">
                <option value="en">🇺🇸 English</option>
                <option value="es">🇲🇽 Spanish</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Auto-detect tenant language preference</p>
                <p className="text-xs text-gray-400 mt-0.5">Use Spanish for tenants with preferredLanguage: es</p>
              </div>
              <Toggle checked={settings.autoDetectLanguage} onChange={v => set('autoDetectLanguage', v)} />
            </div>
          </div>
        </div>

        {/* Section 4: Voice Agent (ElevenLabs) */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="font-semibold text-gray-900">🎙️ Voice Agent (ElevenLabs)</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Configure your ElevenLabs AI agent and outbound phone identity for live voice calls.
                <a href="https://elevenlabs.io/conversational-ai" target="_blank" rel="noreferrer"
                  className="ml-1 text-tenafi-green hover:underline">Get an agent →</a>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-400">{voiceConfig.voiceEnabled ? 'Enabled' : 'Disabled'}</span>
              <button
                onClick={() => setVoice('voiceEnabled', !voiceConfig.voiceEnabled)}
                type="button"
                className={"relative inline-flex h-6 w-11 items-center rounded-full transition-colors " +
                  (voiceConfig.voiceEnabled ? 'bg-tenafi-green' : 'bg-gray-200')}>
                <span className={"inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
                  (voiceConfig.voiceEnabled ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <SecretInput
              label="ElevenLabs API Key"
              value={voiceConfig.elevenlabsApiKey}
              onChange={v => setVoice('elevenlabsApiKey', v)}
              placeholder="xi-api-key-..."
              hint="Found in ElevenLabs → Profile → API Keys"
            />
            <SecretInput
              label="ElevenLabs Agent ID"
              value={voiceConfig.elevenlabsAgentId}
              onChange={v => setVoice('elevenlabsAgentId', v)}
              placeholder="agent_..."
              hint="Found in ElevenLabs → Conversational AI → your agent → Agent ID"
            />
            <SecretInput
              label="ElevenLabs Phone Number ID"
              value={voiceConfig.elevenlabsPhoneNumberId}
              onChange={v => setVoice('elevenlabsPhoneNumberId', v)}
              placeholder="pn_..."
              hint="Required by the current ElevenLabs outbound-call API. Found under your linked phone numbers."
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Legacy Caller ID Fallback</label>
              <input
                type="tel"
                value={voiceConfig.twilioFromNumber}
                onChange={e => setVoice('twilioFromNumber', e.target.value)}
                placeholder="+15551234567"
                className="w-full sm:w-64 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-mono focus:ring-2 focus:ring-tenafi-green outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">Optional E.164 fallback if you are still using the older Twilio-from-number flow.</p>
            </div>

            {/* Webhook URL section */}
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Post-Call Webhook URL</p>
                <button
                  type="button"
                  onClick={() => setShowWebhookUrl(v => !v)}
                  className="text-xs text-tenafi-green hover:underline"
                >
                  {showWebhookUrl ? 'Hide' : 'Show'}
                </button>
              </div>
              {showWebhookUrl ? (
                <div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2 flex-1 break-all font-mono">
                      {webhookUrl}
                    </code>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(webhookUrl); }}
                      className="text-xs text-tenafi-green hover:underline whitespace-nowrap font-medium"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Paste this into your ElevenLabs agent → Settings → Post-call webhook so transcripts and call outcomes flow back into Tenafi.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  Paste this URL into your ElevenLabs agent settings so call transcripts are logged back to Tenafi.
                </p>
              )}
            </div>

            {/* Config checklist */}
            <div className="rounded-xl bg-tenafi-light border border-tenafi-green/20 p-4">
              <p className="text-xs font-semibold text-tenafi-green mb-2">Setup Checklist</p>
              <ul className="space-y-1.5 text-xs">
                {[
                  { done: !!voiceConfig.elevenlabsApiKey, text: 'ElevenLabs API key entered' },
                  { done: !!voiceConfig.elevenlabsAgentId, text: 'Agent ID entered' },
                  { done: !!voiceConfig.elevenlabsPhoneNumberId, text: 'ElevenLabs phone number ID entered' },
                  { done: !!voiceConfig.twilioFromNumber, text: 'Optional caller ID fallback entered' },
                  { done: voiceConfig.voiceEnabled, text: 'Voice calling enabled' },
                ].map(({ done, text }) => (
                  <li key={text} className={`flex items-center gap-2 ${done ? 'text-tenafi-green' : 'text-gray-400'}`}>
                    <span>{done ? '✓' : '○'}</span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button onClick={handleSaveVoice} disabled={savingVoice}
              className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50">
              {savingVoice ? 'Saving...' : 'Save Voice Settings'}
            </button>
            {savedVoice && (
              <span className="text-sm text-green-600 font-medium">✓ Saved!</span>
            )}
          </div>
        </div>

        {/* Section 5: Agent Behavior Configuration */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900">🤖 Agent Behavior</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Control how the AI agent communicates, escalates, and tracks promises across all channels.
            </p>
          </div>

          {/* ── 5a: Contact Frequency Limits ── */}
          <div className="mb-7">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Contact Frequency Limits</h3>
            <p className="text-xs text-gray-400 mb-4">Maximum contacts per tenant before the agent pauses outreach and flags for manual review.</p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { key: 'maxCallsPerTenant', label: 'Max Calls / Tenant', unit: 'calls' },
                { key: 'maxSmsPerWeek',     label: 'Max SMS / Week',     unit: 'messages' },
                { key: 'maxEmailsPerWeek',  label: 'Max Emails / Week',  unit: 'emails' },
              ].map(({ key, label, unit }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1} max={30}
                      value={agentConfig[key]}
                      onChange={e => setAgent(key, parseInt(e.target.value) || 1)}
                      className="w-20 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
                    />
                    <span className="text-xs text-gray-400">{unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 mb-7" />

          {/* ── 5b: Risk-Based Tone Instructions ── */}
          <div className="mb-7">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Risk-Based Tone</h3>
            <p className="text-xs text-gray-400 mb-4">
              Custom instructions sent to the AI for each risk tier. The agent will follow these when writing messages or conducting calls.
            </p>
            <div className="space-y-4">
              {[
                { tier: 'low',    label: '🟢 Low Risk',    bg: 'bg-green-50 border-green-200'  },
                { tier: 'medium', label: '🟡 Medium Risk',  bg: 'bg-amber-50 border-amber-200'  },
                { tier: 'high',   label: '🔴 High Risk',   bg: 'bg-red-50 border-red-200'      },
              ].map(({ tier, label, bg }) => (
                <div key={tier} className={`rounded-xl border p-4 ${bg}`}>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">{label}</label>
                  <textarea
                    rows={2}
                    value={agentConfig.toneInstructions[tier]}
                    onChange={e => setAgentTone(tier, e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-tenafi-green outline-none resize-none"
                    placeholder={`Instructions for ${tier}-risk tenants...`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 mb-7" />

          {/* ── 5c: Escalation Rules ── */}
          <div className="mb-7">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Escalation Rules</h3>
            <p className="text-xs text-gray-400 mb-4">
              The agent will immediately flag a tenant for manual review when these signals are detected.
            </p>

            {/* Signal toggles */}
            <div className="space-y-3 mb-5">
              {[
                { key: 'escalateOnDispute',     label: 'Escalate on dispute signals',      desc: 'Tenant disputes the charge or says they already paid' },
                { key: 'escalateOnLegalThreat', label: 'Escalate on legal threats',         desc: 'Tenant mentions lawsuit, attorney, or court action' },
                { key: 'escalateOnHardship',    label: 'Escalate on financial hardship',    desc: 'Tenant mentions job loss, illness, or inability to pay' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                  </div>
                  <Toggle checked={agentConfig[key]} onChange={v => setAgent(key, v)} />
                </div>
              ))}
            </div>

            {/* Auto-escalate after N days */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Auto-escalate after</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={1} max={90}
                  value={agentConfig.autoEscalateAfterDays}
                  onChange={e => setAgent('autoEscalateAfterDays', parseInt(e.target.value) || 14)}
                  className="w-24 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
                />
                <span className="text-sm text-gray-500">days with no response</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Tenant is flagged for human follow-up after this many days of silence.</p>
            </div>

            {/* Custom escalation keywords */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Custom escalation keywords</label>
              <p className="text-xs text-gray-400 mb-2">If any of these words appear in a tenant reply, the tenant is escalated immediately.</p>
              <TagInput
                tags={agentConfig.escalationKeywords}
                onChange={v => setAgent('escalationKeywords', v)}
                placeholder="e.g. mold, roaches, uninhabitable"
              />
            </div>

            {/* Forbidden language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Forbidden language</label>
              <p className="text-xs text-gray-400 mb-2">The agent will never use these words or phrases in any outreach message.</p>
              <TagInput
                tags={agentConfig.forbiddenLanguage}
                onChange={v => setAgent('forbiddenLanguage', v)}
                placeholder="e.g. eviction notice, collections agency"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 mb-7" />

          {/* ── 5d: Promise Tracking ── */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Promise Tracking</h3>
            <p className="text-xs text-gray-400 mb-4">
              When a tenant promises to pay by a date, the agent monitors the promise and escalates if payment doesn't arrive.
            </p>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Allow promises to pay</p>
                  <p className="text-xs text-gray-400 mt-0.5">Agent accepts "I'll pay by Friday" type responses and tracks them</p>
                </div>
                <Toggle checked={agentConfig.allowPromisesToPay} onChange={v => setAgent('allowPromisesToPay', v)} />
              </div>
              <div className={"transition-opacity " + (!agentConfig.allowPromisesToPay ? 'opacity-40 pointer-events-none' : '')}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Max promise window</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min={1} max={30}
                    value={agentConfig.maxPromiseWindowDays}
                    onChange={e => setAgent('maxPromiseWindowDays', parseInt(e.target.value) || 7)}
                    className="w-24 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">If payment doesn't arrive within this window after the promised date, tenant is auto-escalated.</p>
              </div>
            </div>
          </div>

          {/* Save button */}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={handleSaveAgent} disabled={savingAgent}
              className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50">
              {savingAgent ? 'Saving...' : 'Save Agent Config'}
            </button>
            {savedAgent && (
              <span className="text-sm text-green-600 font-medium">✓ Saved!</span>
            )}
          </div>
        </div>

        {/* Section 6: Autopilot */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="font-semibold text-gray-900">🤖 Autopilot</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Automatically send follow-up outreach to delinquent tenants when they go silent.
                The system runs nightly and respects your contact frequency limits.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-400">{autopilot.enabled ? 'On' : 'Off'}</span>
              <Toggle checked={autopilot.enabled} onChange={v => setAuto('enabled', v)} />
            </div>
          </div>

          <div className={"space-y-6 transition-opacity " + (!autopilot.enabled ? 'opacity-40 pointer-events-none' : '')}>

            {/* Follow-up delay */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Follow-up after silence of</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={1} max={30}
                  value={autopilot.followUpDelayDays}
                  onChange={e => setAuto('followUpDelayDays', parseInt(e.target.value) || 3)}
                  className="w-20 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
                />
                <span className="text-sm text-gray-500">days since last outreach with no response</span>
              </div>
            </div>

            {/* Max auto follow-ups */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Max auto follow-ups per tenant per month</label>
              <div className="flex gap-2">
                {[1, 2, 3, 5].map(n => (
                  <button key={n} type="button"
                    onClick={() => setAuto('maxAutoFollowUps', n)}
                    className={`px-4 py-1.5 rounded-xl text-sm font-medium transition-colors border ${
                      autopilot.maxAutoFollowUps === n
                        ? 'bg-tenafi-green text-white border-tenafi-green'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-tenafi-green hover:text-tenafi-green'
                    }`}>
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Agent stops and flags the tenant for manual review after hitting this cap.</p>
            </div>

            {/* Channel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Auto follow-up channel</label>
              <div className="flex gap-2">
                {[{ val: 'sms', label: '📱 SMS' }, { val: 'email', label: '📧 Email' }].map(ch => (
                  <button key={ch.val} type="button"
                    onClick={() => setAuto('channel', ch.val)}
                    className={`px-4 py-1.5 rounded-xl text-sm font-medium transition-colors border ${
                      autopilot.channel === ch.val
                        ? 'bg-tenafi-green text-white border-tenafi-green'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-tenafi-green hover:text-tenafi-green'
                    }`}>
                    {ch.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Risk tiers to include */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Include risk tiers</label>
              <div className="space-y-3">
                {[
                  { key: 'includeHighRisk',   label: '🔴 High Risk',   desc: 'Tenants with score 85+' },
                  { key: 'includeMediumRisk', label: '🟡 Medium Risk', desc: 'Tenants with score 60–84' },
                  { key: 'includeLowRisk',    label: '🟢 Low Risk',    desc: 'Tenants with score below 60' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{label}</p>
                      <p className="text-xs text-gray-400">{desc}</p>
                    </div>
                    <Toggle checked={autopilot[key]} onChange={v => setAuto(key, v)} />
                  </div>
                ))}
              </div>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800">
              <p className="font-semibold mb-1">ℹ️ How Autopilot Works</p>
              <ul className="space-y-1 list-disc list-inside text-blue-700">
                <li>Runs hourly in the background and only sends once the tenant reaches the local-safe send window</li>
                <li>Respects your quiet hours defaults, tenant local time, and SMS opt-out status</li>
                <li>Skips tenants who already received outreach within the follow-up window</li>
                <li>Respects contact frequency caps set in Agent Behavior</li>
                <li>Uses Risk-Based Tone instructions for each message</li>
                <li>All messages are logged to the outreach log for your review</li>
              </ul>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={handleSaveAutopilot} disabled={savingAutopilot}
              className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50">
              {savingAutopilot ? 'Saving...' : 'Save Autopilot Settings'}
            </button>
            {savedAutopilot && (
              <span className="text-sm text-green-600 font-medium">✓ Saved!</span>
            )}
            {autopilot.enabled && (
              <button
                onClick={async () => {
                  setRunningAutopilot(true);
                  setAutopilotRunResult(null);
                  try {
                    const fns = getFunctions();
                    const run = httpsCallable(fns, 'runAutopilotNow');
                    const res = await run({});
                    setAutopilotRunResult(res.data);
                  } catch (err) {
                    setAutopilotRunResult({ error: err.message });
                  } finally {
                    setRunningAutopilot(false);
                  }
                }}
                disabled={runningAutopilot}
                className="px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {runningAutopilot
                  ? <><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Running...</>
                  : '▶ Run Now'}
              </button>
            )}
            {autopilotRunResult && !autopilotRunResult.error && (
              <span className="text-sm text-green-600 font-medium">
                ✓ Sent {autopilotRunResult.sent || 0}, skipped {autopilotRunResult.skipped || 0}
              </span>
            )}
            {autopilotRunResult?.error && (
              <span className="text-sm text-red-500">{autopilotRunResult.error}</span>
            )}
          </div>
        </div>

        {/* Section 7: Notifications (coming soon) */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 opacity-60">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Notifications</h2>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full font-medium">Coming Soon</span>
          </div>
          <p className="text-sm text-gray-500">
            Get daily email summaries, Slack notifications when high-risk tenants are flagged, and weekly collection reports.
          </p>
        </div>

        {/* Save button */}
        <div className="flex justify-end mb-8">
          <button onClick={handleSave} disabled={saving}
            className="px-6 py-3 bg-tenafi-green text-white rounded-xl font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl border border-red-200 p-6">
          <h2 className="font-semibold text-red-600 mb-2">Danger Zone</h2>
          <p className="text-sm text-gray-500 mb-4">Type DELETE to confirm deleting all your data. This cannot be undone.</p>
          <div className="flex gap-3 items-center">
            <input type="text" value={deleteConfirm} onChange={e => { setDeleteConfirm(e.target.value); setDeleteError(''); }}
              placeholder="Type DELETE to confirm"
              className="border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-red-400 outline-none" />
            <button
              onClick={() => {
                if (deleteConfirm !== 'DELETE') { setDeleteError('You must type DELETE exactly.'); return; }
                alert('Delete all data is disabled in demo mode.');
              }}
              className="px-4 py-2.5 border border-red-300 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors">
              Delete All Data
            </button>
          </div>
          {deleteError && <p className="text-red-500 text-xs mt-2">{deleteError}</p>}
        </div>
      </div>
    </div>
  );
}
